/**
 * Inactivity Warning Modal
 *
 * Accurate countdown based on wall-clock remaining ms.
 * Progress is relative to the fixed warning window (not a drifting first paint).
 */

import React, { useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

const InactivityWarningModal = ({
  isVisible,
  timeRemaining = 0,
  formattedTimeRemaining = '0:00',
  warningDurationMs = 120000,
  onExtendSession,
  onLogoutNow,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const messageId = useId();

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onExtendSession?.();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isVisible, onExtendSession]);

  if (!isVisible) {
    return null;
  }

  const safeRemaining = Math.max(0, timeRemaining);
  const totalSeconds = Math.max(0, Math.ceil(safeRemaining / 1000));
  const isCritical = safeRemaining > 0 && safeRemaining <= 30000;
  const progressPercent = warningDurationMs > 0
    ? Math.max(0, Math.min(100, (safeRemaining / warningDurationMs) * 100))
    : 0;

  return createPortal(
    <div className="inactivity-modal-overlay" role="presentation">
      <div
        className={`inactivity-modal-content${isCritical ? ' inactivity-modal-content--critical' : ''}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
      >
        <div className="inactivity-modal-icon" aria-hidden="true">
          <span className="material-symbols-outlined">
            {isCritical ? 'timer' : 'hourglass_top'}
          </span>
        </div>

        <h2 id={titleId} className="inactivity-modal-title">
          {t('sessionTimeoutWarning', {
            ns: 'common',
            defaultValue: 'Are you still there?',
          })}
        </h2>

        <p id={messageId} className="inactivity-modal-message">
          {t('sessionTimeoutMessage', {
            ns: 'common',
            defaultValue:
              'Your session will end soon because of inactivity. Stay logged in to continue, or log out now.',
          })}
        </p>

        <div className="inactivity-modal-timer-card" aria-live="polite" aria-atomic="true">
          <span className="inactivity-modal-timer-label">
            {t('timeRemaining', {
              ns: 'common',
              defaultValue: 'Logging out in',
            })}
          </span>
          <span className="inactivity-modal-timer-value" data-testid="inactivity-countdown">
            {formattedTimeRemaining}
          </span>
          <p className="inactivity-modal-timer-hint">
            {t('secondsRemaining', {
              ns: 'common',
              count: totalSeconds,
              defaultValue:
                totalSeconds === 1
                  ? '1 second remaining'
                  : `${totalSeconds} seconds remaining`,
            })}
          </p>
          <div
            className="inactivity-modal-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progressPercent)}
            aria-label={t('timeRemaining', { ns: 'common', defaultValue: 'Time remaining' })}
          >
            <div
              className="inactivity-modal-progress-bar"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="inactivity-modal-actions">
          <button
            type="button"
            className="inactivity-modal-btn inactivity-modal-btn--primary"
            onClick={onExtendSession}
            autoFocus
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              check_circle
            </span>
            {t('extendSession', { ns: 'common', defaultValue: 'Stay logged in' })}
          </button>

          <button
            type="button"
            className="inactivity-modal-btn inactivity-modal-btn--secondary"
            onClick={onLogoutNow}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              logout
            </span>
            {t('logoutNow', { ns: 'common', defaultValue: 'Log out now' })}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InactivityWarningModal;
